import { useEffect, useRef, useState } from "react";

import { on, off, type ViewerBusEvents } from "../lib/viewerBus";

const AUTO_DISMISS_MS = 3500;

/**
 * PageToast — the single page-level transient-notification host. Subscribes to
 * the viewerBus `toast` channel and renders the latest message top-center,
 * fixed to the viewport so it shows on every page — the standalone reporter
 * (`/prototype-report`) as well as the viewer grid. Each emit re-arms the
 * auto-dismiss timer, so repeating the same message re-shows it (a shared
 * "current value" atom could not — an unchanged value triggers no re-render).
 *
 * Toasts carrying an `action` render an inline button (e.g. bulk-delete Undo);
 * pressing it runs the action and dismisses immediately. `durationMs` lets
 * those emits extend the dismiss window so the rad has time to react.
 *
 * Mounted ONCE at the App layout. Do not mount per-page: the labeling viewer
 * and its embedded reporter would otherwise each render a host and double the
 * toast.
 */
export const PageToast = () => {
  const [toast, setToast] = useState<ViewerBusEvents["toast"] | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const handler = on("toast", (payload) => {
      setToast(payload);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        setToast(null);
        timerRef.current = null;
      }, payload.durationMs ?? AUTO_DISMISS_MS);
    });
    return () => {
      off("toast", handler);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  if (!toast) return null;

  const dismiss = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setToast(null);
  };

  return (
    <div className="fixed top-14 left-1/2 -translate-x-1/2 z-60 pointer-events-none">
      <div className="bg-bg-2/90 px-3 py-1.5 rounded text-xs text-fg border border-border-strong whitespace-nowrap">
        {toast.message}
        {toast.action ? (
          <button
            type="button"
            className="ml-2 underline font-medium pointer-events-auto"
            onClick={() => {
              toast.action?.onPress();
              dismiss();
            }}
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>
    </div>
  );
};
